import React from 'react';
import routes from '@/services/routes';

import { HeadingSizes } from '@/components/commons/page-elements/Heading';

import EnvelopeSolid from '@/components/commons/icons/EnvelopeSolid';
import UsersGroup from '@/components/commons/icons/UsersGroup';
import CalendarMonth from '@/components/commons/icons/CalendarMonth';
import Reply from '@/components/commons/icons/Reply';
import WandMagicSparkles from '@/components/commons/icons/WandMagicSparkles';

// -------------------------------------------------------------------------------------------------
// IMPORTANT!  READ BEFORE MODIFYING THIS FILE!!!
//
// This file is used to store the data for the benefits cards that are displayed on the onboarding
// pages.  We want to track the efficacy of what people are shown.  As such, you should NOT modify
// the contents here.  You should _add new entries_ to this file, and then reference those new
// entries from the sign-up page.  The only time you should change existing content is to correct
// trivial typos, or make aesthetic changes that are part of a larger design update but which don't
// substantively change the content.
//
// If you need to change a shared element in a way that would affect multiple pages, you should
// make a new const for the updated version, and reference that in new versions of a card
// configuration!
// -------------------------------------------------------------------------------------------------

const signUpNowButton = {
    key: 'signUpNowButton',
    buttonText: 'Sign up now (in less than 60 seconds)',
    buttonIcon: <WandMagicSparkles className="h-[12px] w-[12px] mb-[1px] mr-2 text-white" />,
    buttonRoute: routes.CHOOSE_METHOD,
    fullSizeButton: true,
};

const smallerInboxCard = {
    key: 'smallerInboxCard',
    icon: <EnvelopeSolid className="min-w-20 md:w-40 mb-4 text-cleanSlate" />,
    boldText: 'We make your inbox 90% smaller',
    plainText: 'Sixty eliminates the noise from your inbox, bringing order to your world and helping you focus on what’s really important.',
};

const shrinkInboxCard = {
    key: smallerInboxCard.key,
    icon: smallerInboxCard.icon,
    boldText: 'We shrink your inbox by 90%',
    plainText: smallerInboxCard.plainText,
};

const followUpCard = {
    key: 'followUpCard',
    icon: <Reply className="min-w-20 md:w-40 mb-4 text-cleanSlate" />,
    boldText: 'We help you follow up',
    plainText: 'Never miss an opportunity to follow up after a meeting, summarizing important takeaways or finalizing next steps.',
};

const betterMeetingsCard = {
    key: 'betterMeetingsCard',
    icon: <CalendarMonth className="min-w-20 md:w-40 mb-4 text-cleanSlate" />,
    boldText: 'We make your meetings better',
    plainText: 'You’ll arrive prepared for meetings, having been briefed on who you’re meeting with, and having shared your brief as well.',
};

const benefitCardsData = {
    generic_v1: {
        cardProps: {
            heading: {
                children: 'What can Sixty do for you?',
                bold: true,
                size: HeadingSizes.H3,
                externalStyles: 'flex justify-start items-center text-center sm:text-left',
                icon: <WandMagicSparkles className="h-[40px] mr-0 sm:mr-2 mb-2 sm:mb-0 text-cleanSlate" />,

            },
            body: [{
                key: 'meaningfulConnectionsBody',
                text: 'Sixty brings meaning to your personal connections and gives back hours of your day.',
                style: 'mt-5 font-bold',
            },
            {
                key: 'saveTimeBody',
                text: 'Before you know it, you’ll be enjoying the right conversations with the right people, and you’ll have saved hours usually spent on daily productivity operations. Here’s how we do it.',
                style: 'mb-5',
            }],
            externalStyles: 'm-2 gap-4 md:mx-5 md:my-6',
            buttons: [signUpNowButton],
        },
        internalCards: [
            smallerInboxCard,
            {
                key: 'growNetworkCard',
                icon: <UsersGroup className="min-w-20 md:w-40 mb-4 text-cleanSlate" />,
                boldText: 'We exponentially grow your network',
                plainText: 'Connect your personal network to the networks of everyone you know, supercharging your access to the right people.',
            },
            betterMeetingsCard,
            {
                key: followUpCard.key,
                icon: followUpCard.icon,
                boldText: 'We help you effortlessly follow up',
                plainText: followUpCard.plainText,
            },
        ],
    },
    calendly_v1: {
        cardProps: {
            heading: {
                children: 'Sixty makes your meetings better',
                bold: true,
                size: HeadingSizes.H3,
                externalStyles: 'flex justify-start items-center text-center sm:text-left',
                icon: <CalendarMonth className="h-[40px] mr-0 sm:mr-2 mb-2 sm:mb-0 text-cleanSlate" />,
            },
            body: [{
                key: 'arrivePreparedBody',
                text: 'With Sixty, you’ll arrive prepared for meetings, having been briefed on who you’re meeting with, and having shared your brief as well.',
                style: 'mt-5 font-bold',
            },
            {
                key: 'saveTimeBody',
                text: 'Before you know it, you’ll be enjoying the right conversations with the right people, and you’ll have saved hours usually spent on daily productivity operations.',
                style: 'mb-6',
            },
            {
                key: 'thatsNotAllBody',
                text: 'But that isn’t all we’ll do for you!',
                style: 'mb-4',
            }],
            externalStyles: 'm-2 gap-4 md:mx-5 md:my-6',
            buttons: [signUpNowButton],
        },
        internalCards: [
            shrinkInboxCard,
            {
                key: 'expandNetworkCard',
                icon: <UsersGroup className="min-w-20 md:w-40 mb-4 text-cleanSlate" />,
                boldText: 'We expand your network',
                plainText: 'Connect your personal network to the networks of everyone you know, supercharging your access to the right people.',
            },
            followUpCard,
        ],
    },
    linkedin_v1: {
        cardProps: {
            heading: {
                children: 'Sixty exponentially grows your network',
                bold: true,
                size: HeadingSizes.H3,
                externalStyles: 'flex justify-start items-center text-center sm:text-left',
                icon: <UsersGroup className="h-[40px] mr-0 sm:mr-2 mb-2 sm:mb-0 text-cleanSlate" />,
            },
            body: [{
                key: 'connectPersonalNetworkBody',
                text: 'Sixty will connect your personal network to the networks of everyone you know, supercharging your access to the right people.',
                style: 'mt-5 font-bold',
            },
            {
                key: 'saveTimeBody',
                text: 'Before you know it, you’ll be enjoying the right conversations with the right people, and you’ll have saved hours usually spent on daily productivity operations.',
                style: 'mb-6',
            },
            {
                key: 'thatsNotAllBody',
                text: 'But that isn’t all we’ll do for you!',
                style: 'mb-4',
            }],
            externalStyles: 'm-2 gap-4 md:mx-5 md:my-6',
            buttons: [signUpNowButton],
        },
        internalCards: [
            shrinkInboxCard,
            {
                key: betterMeetingsCard.key,
                icon: betterMeetingsCard.icon,
                boldText: 'We make meetings better',
                plainText: betterMeetingsCard.plainText,
            },
            followUpCard,
        ],
    },
};

export default function benefitsCardDataResolver(dataElement) {
    return benefitCardsData[dataElement] || null;
}
